import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { today, normalizeDateSystem, toIsoDay } from '../tools/calendars';
import { INITIAL as CALC_INITIAL } from '../tools/floatingTools/calcEngine';

// The floating tools - a calendar (F5) and a calculator (F6) - and the little bit of state they share with the app.
//
// Two contexts on purpose:
//   · ToolsRegistryContext  - setTarget / clearTarget, and nothing else. Its value NEVER changes, so a section that
//     registers its date filter (useDateFilterTarget) is not re-rendered every time a tool opens, moves or is typed in.
//   · ToolsStateContext     - everything the windows themselves draw from.
//
// Where state lives matters: it is kept HERE, above the sections, so a half-typed sum and a picked date range survive
// closing the window, opening it again, and moving to another section - and on a phone, where the tool is a page on
// top of the section, dismissing it leaves the section underneath exactly as it was (it was never unmounted).
// Toggling a tool NEVER erases what is in it: the sum, the memory, the history, the picked range, the month showing and
// the view (days / months / years) are all here, and are also kept for the browser tab (sessionStorage), so a reload
// keeps them too.
//
// The KEYBOARD: the calculator owns the keys from the moment it is opened (F6, the phone menu, or a click in it) until the
// person clicks or tabs to something outside the tool windows, or closes it - even when a form field still has the cursor
// (a tool never takes the focus from a form: see modalIsOpen in tools/floatingTools/toolStyles.js). FloatingTools routes
// the keys; `kbOwner` here is who they go to.

const SYSTEM_KEY = 'xms_tools_calSystem';
const RECTS_KEY = 'xms_tools_rects';
const STATE_KEY = 'xms_tools_state';        // sessionStorage: the content of the tools, for this browser tab
export const TOOL_NAMES = ['calendar', 'calculator'];

const read = (key) => { try { return localStorage.getItem(key); } catch (_) { return null; } };
const write = (key, value) => {
  try { if (value == null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch (_) { /* private window: the tools still work, they just forget */ }
};

const finite = (n) => typeof n === 'number' && Number.isFinite(n);
function readRects() {
  try {
    const raw = JSON.parse(read(RECTS_KEY) || '{}');
    const out = {};
    TOOL_NAMES.forEach((name) => {
      const r = raw && raw[name];
      if (r && finite(r.x) && finite(r.y) && finite(r.w) && finite(r.h)) out[name] = { x: r.x, y: r.y, w: r.w, h: r.h };
    });
    return out;
  } catch (_) { return {}; }
}

// ── what the tools hold, kept for the tab ───────────────────────────────────────────────────────────────────────────
const readSession = () => { try { return JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null') || {}; } catch (_) { return {}; } };
const writeSession = (value) => { try { sessionStorage.setItem(STATE_KEY, JSON.stringify(value)); } catch (_) { /* no storage: nothing is kept across a reload */ } };
const asDay = (iso) => { if (typeof iso !== 'string') return null; const d = new Date(iso); return Number.isNaN(d.getTime()) ? null : d; };
const EXPR = /^[0-9.+\-*/()%]*$/;
const VIEWS = ['days', 'months', 'years'];

// A saved calculator, checked as if a stranger had written it: only what the engine could have produced comes back.
function validCalc(saved) {
  const c = saved && typeof saved === 'object' ? saved : {};
  const text = (v) => (typeof v === 'string' && v.length <= 400 && EXPR.test(v) ? v : '');
  const history = Array.isArray(c.history)
    ? c.history.filter((h) => h && typeof h.expr === 'string' && EXPR.test(h.expr) && typeof h.result === 'string' && /^-?[0-9.]+$/.test(h.result)).slice(0, 30).map((h) => ({ expr: h.expr, result: h.result }))
    : [];
  const memory = typeof c.memory === 'number' && Number.isFinite(c.memory) ? c.memory : null;
  return { ...CALC_INITIAL, expr: text(c.expr), prev: text(c.prev), evaluated: c.evaluated === true && text(c.expr) !== '', history, memory };
}

const ToolsStateContext = createContext(null);
const ToolsRegistryContext = createContext(null);

export function ToolsProvider({ children }) {
  // Which tools are open, in stacking order (the last one is in front).
  const [open, setOpen] = useState([]);
  // On a phone a tool is a page, and a page replaces the one before it: opening one closes the other.
  const mobileRef = useRef(false);

  const saved = useRef(null);
  if (saved.current === null) saved.current = readSession();
  const [cal, setCal] = useState(() => {
    const stored = read(SYSTEM_KEY);
    const c = saved.current.cal || {};
    return {
      system: stored ? normalizeDateSystem(stored) : '',
      cursor: asDay(c.cursor) || today(),
      from: asDay(c.from), to: asDay(c.to),
      view: VIEWS.includes(c.view) ? c.view : 'days',     // days | months | years - kept here so closing the window does not reset it
    };
  });
  const [calc, setCalc] = useState(() => validCalc(saved.current.calc));
  const [calcPanel, setCalcPanel] = useState(Boolean(saved.current.calcPanel));   // the calculator's history list is showing
  const [rects, setRects] = useState(readRects);

  useEffect(() => {
    writeSession({
      cal: { cursor: toIsoDay(cal.cursor), from: cal.from ? toIsoDay(cal.from) : null, to: cal.to ? toIsoDay(cal.to) : null, view: cal.view },
      calc: { expr: calc.expr, prev: calc.prev, evaluated: calc.evaluated, history: calc.history, memory: calc.memory },
      calcPanel,
    });
  }, [cal, calc, calcPanel]);

  // Who the keyboard belongs to: 'calculator' or null (the page underneath / the calendar's own focus). The calculator takes it
  // the moment it opens; a tool opening after it that uses the focus (the calendar) takes it away; closing the owner ends it.
  const [kbOwner, setKbOwner] = useState(null);
  const seenOpen = useRef([]);
  useEffect(() => {
    const was = seenOpen.current;
    seenOpen.current = open;
    const added = open.filter((n) => !was.includes(n));
    if (added.includes('calculator')) setKbOwner('calculator');
    else if (added.length) setKbOwner(null);
    else setKbOwner((cur) => (cur && open.includes(cur) ? cur : null));
  }, [open]);

  const openTool = useCallback((name) => {
    setOpen((prev) => (mobileRef.current ? [name] : [...prev.filter((n) => n !== name), name]));
  }, []);
  const closeTool = useCallback((name) => setOpen((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : prev)), []);
  const closeAll = useCallback(() => setOpen((prev) => (prev.length ? [] : prev)), []);
  const toggle = useCallback((name) => {
    setOpen((prev) => {
      if (prev.includes(name)) return prev.filter((n) => n !== name);
      return mobileRef.current ? [name] : [...prev, name];
    });
  }, []);
  const raise = useCallback((name) => {
    setOpen((prev) => (!prev.includes(name) || prev[prev.length - 1] === name ? prev : [...prev.filter((n) => n !== name), name]));
  }, []);
  const setMobile = useCallback((isMobile) => {
    mobileRef.current = Boolean(isMobile);
    if (isMobile) setOpen((prev) => (prev.length > 1 ? [prev[prev.length - 1]] : prev));
  }, []);

  // The calendar's own state: which calendar, which month is showing (any day in it), the picked range.
  const patchCal = useCallback((patch) => {
    if (patch.system) write(SYSTEM_KEY, normalizeDateSystem(patch.system));
    setCal((c) => ({ ...c, ...patch }));
  }, []);

  // Window rectangles. A move or a resize updates them live; only when it ends are they written to storage.
  const rectsRef = useRef(rects);
  rectsRef.current = rects;
  const setRect = useCallback((name, rect) => setRects((r) => ({ ...r, [name]: rect })), []);
  const commitRects = useCallback(() => write(RECTS_KEY, JSON.stringify(rectsRef.current)), []);
  const resetRect = useCallback((name) => {
    const next = { ...rectsRef.current };
    delete next[name];
    rectsRef.current = next;
    setRects(next);
    write(RECTS_KEY, JSON.stringify(next));
  }, []);

  // ── date-filter targets: the screens that can take a date range from the calendar ──────────────────────────────
  const targetsRef = useRef(new Map());
  const seqRef = useRef(0);
  const [target, setTargetState] = useState(null);

  const publish = useCallback(() => {
    let top = null;
    targetsRef.current.forEach((entry, id) => { if (!top || entry.seq > top.seq) top = { ...entry, id }; });
    setTargetState((prev) => {
      if (!top) return prev === null ? prev : null;
      if (prev && prev.id === top.id && prev.label === top.label && prev.detail === top.detail && prev.apply === top.apply) return prev;
      return { id: top.id, label: top.label, detail: top.detail, apply: top.apply };
    });
  }, []);
  const setTarget = useCallback((id, entry) => {
    const prev = targetsRef.current.get(id);
    targetsRef.current.set(id, { ...entry, seq: prev ? prev.seq : (seqRef.current += 1) });
    publish();
  }, [publish]);
  const clearTarget = useCallback((id) => {
    if (targetsRef.current.delete(id)) publish();
  }, [publish]);

  const registry = useMemo(() => ({ setTarget, clearTarget }), [setTarget, clearTarget]);

  const state = useMemo(() => ({
    open, openTool, closeTool, closeAll, toggle, raise, setMobile,
    cal, patchCal, calc, setCalc, calcPanel, setCalcPanel, kbOwner, setKbOwner,
    rects, setRect, commitRects, resetRect,
    target,
  }), [open, openTool, closeTool, closeAll, toggle, raise, setMobile, cal, patchCal, calc, calcPanel, kbOwner, rects, setRect, commitRects, resetRect, target]);

  return (
    <ToolsRegistryContext.Provider value={registry}>
      <ToolsStateContext.Provider value={state}>{children}</ToolsStateContext.Provider>
    </ToolsRegistryContext.Provider>
  );
}

/** The tools' state and actions, for the windows, the shortcut keys and the menu that opens them. */
export function useTools() {
  const ctx = useContext(ToolsStateContext);
  if (!ctx) throw new Error('useTools must be used inside <ToolsProvider>');
  return ctx;
}

let targetCounter = 0;

/**
 * A screen with a date filter says so, and says how to set it; the calendar window then offers "Apply to <label>".
 *
 *   useDateFilterTarget({
 *     label: t('nav.customers'),                 // what the button names
 *     detail: t('crm.lastCallRange'),            // optional: WHICH date it filters by
 *     enabled: view === 'customers',             // register only while this filter is the one on screen
 *     apply: ({ from, to, system }) => ...,      // from / to are Gregorian 'YYYY-MM-DD' ('' = clear); system is the calendar the
 *   });                                          // person picked them in, for a screen that can show its filter in that calendar
 *
 * Outside a ToolsProvider it does nothing, so a screen using it can still be rendered on its own.
 */
export function useDateFilterTarget({ label, detail, apply, enabled = true }) {
  const registry = useContext(ToolsRegistryContext);
  const idRef = useRef(null);
  if (idRef.current === null) { targetCounter += 1; idRef.current = `dateTarget${targetCounter}`; }
  // The newest apply is called through a ref, so a screen that builds a fresh function every render does not
  // register again every render.
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const stableApply = useCallback((range) => { if (applyRef.current) applyRef.current(range); }, []);

  const setTarget = registry && registry.setTarget;
  const clearTarget = registry && registry.clearTarget;
  useEffect(() => {
    if (!setTarget || !enabled) return undefined;
    const id = idRef.current;
    setTarget(id, { label, detail, apply: stableApply });
    return () => clearTarget(id);
  }, [setTarget, clearTarget, enabled, label, detail, stableApply]);
}
